'use client'

import LanguageSwitcher from '@/components/extension/language-switcher'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { Tabs } from '@/components/ui/tabs'
import { useLocalStorage } from '@/hooks/use-local-storage'
import { useMobileNav } from '@/hooks/zustands/use-mobile-nav'
import { getLastSegment } from '@/lib/utils'
import { AlignJustify, X } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useMemo } from 'react'
import { SearchBar } from '../../search-bar'
import { menuMobileConfig } from '../nav-config'
import { MobileCareShare } from './mobile-care-share'
import { MobileMenuTabItem } from './mobile-menu-tab-item'
import { MobileNavFooter } from './mobile-nav-footer'

const isDisabledLocale = process.env.NEXT_PUBLIC_LOCALE_DISABLED

export function MobileNav() {
  const { isOpen, setIsOpen } = useMobileNav()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const t = useTranslations('header')

  useEffect(() => {
    setIsOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams])

  const options = useMemo(() => {
    const newOptions = Object.entries(menuMobileConfig.menu).map(
      ([category, content]) => ({
        value: category,
        label: t(category).toLocaleUpperCase(),
        gaTrackingValue: `main_nav__tab__${getLastSegment(category)}`,
        gaTrackingLabel: t(category),
        content: (
          <MobileMenuTabItem
            listCategory={content?.sections || []}
            slides={content?.featured || []}
            button={content?.button}
            category={category}
            type={content?.type}
            // mobileBanner={content?.mobileBanner}
          />
        ),
      })
    )

    return newOptions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [activeTab, setActiveTab] = useLocalStorage(
    'mobile-nav-active-tab',
    options[0].value
  )

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <button
          aria-label='Open navigation menu'
          title='Open navigation menu'
          className='flex h-13 w-13 cursor-pointer items-center justify-center'
          role='button'
        >
          <AlignJustify size={20} />
        </button>
      </SheetTrigger>

      <SheetContent
        showCloseButton={false}
        side='left'
        className='scrollbar max-h-screen w-full overflow-y-auto bg-light p-0 pb-24'
        aria-describedby='mobile-nav'
      >
        <SheetTitle className='hidden'></SheetTitle>
        <SheetDescription />

        <div className='w-full space-y-3 bg-neutral-200 p-4'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <Link
                href='/'
                className='flex items-center'
                ga-tracking-value='navbar__logo'
                ga-tracking-label='logo'
              >
                <Image
                  src={'/images/logo-mobile.svg'}
                  alt='Logo Coolmate Mobile'
                  width={30}
                  height={28}
                  className='lg:hidden'
                />
              </Link>
              {!isDisabledLocale && <LanguageSwitcher />}
            </div>
            <Button
              variant='ghost'
              onClick={() => setIsOpen(false)}
              className='fixed right-3 top-3 z-20 flex items-center justify-center bg-neutral-100 p-0'
              size={'icon'}
            >
              <X size={24} />
            </Button>
          </div>
          <SearchBar />

          <div className='rounded-2xl bg-light px-5.5 py-2 pb-6'>
            <Tabs
              variant='neutral'
              wrapperClassName='[&>div>button]:flex-1 [&>div>button]:border-b-[3px] [&>div>button]:px-4'
              options={options}
              className='border-b border-neutral-200 pb-0'
              defaultValue={activeTab}
              onValueChange={setActiveTab}
            />
          </div>
          <MobileCareShare onCloseNav={() => setIsOpen(false)} />
        </div>

        <SheetFooter className='flex-col px-10 py-4 sm:flex-col'>
          {/* {!isLogin && (
            <div className='mb-4 w-full space-y-3 border-b border-b-neutral-200 pb-4'>
              <Button
                asChild
                variant='neutral'
                size='large'
                className='flex w-full'
              >
                <Link href='/login'>Đăng nhập</Link>
              </Button>
              <Button
                asChild
                variant='outline-neutral'
                size='large'
                className='flex w-full'
              >
                <Link href='/register'>Đăng kí</Link>
              </Button>
            </div>
          )} */}

          <MobileNavFooter />
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
